/**
 * "Create jobs now" over a selection of Visits on the board: the command id, its request and result, and
 * the one rule naming why a selected Visit is skipped. The strategy applies the rule to freshly read rows
 * (approval, stamper attention and Job readiness overlaid), so the person sees which Visits were skipped
 * and why instead of one refusal for the whole selection.
 */
import { TaskApprovalStatus } from './task.approval';
import { visitBrowseCapabilities, VisitAgeState, VisitBrowseRow } from './visit.browse';

export const VISIT_CREATE_JOBS_COMMAND = 'VisitCreateJobsCommand';

/**
 * Builds run one after another, due-ordered, as the stamper's single lane does (a line's every-nth-visit
 * ordinal must not be read twice before either Job links). At ~800 ms a build, 25 stays inside one request.
 */
export const MAX_VISIT_CREATE_JOBS = 25;

/** Why a selected Visit got no Job. */
export enum VisitCreateJobSkip {
    HasJob = 'HasJob',
    /** The builder would refuse it: no price book, owner or contact (WBS-12662). */
    MissingJobDetails = 'MissingJobDetails',
    NotApproved = 'NotApproved',
    ApprovalChanged = 'ApprovalChanged',
    PlanPaused = 'PlanPaused',
    Skipped = 'Skipped',
    Missed = 'Missed',
    NeedsReview = 'NeedsReview',
    NoWork = 'NoWork',
    /** Fallback for a row outside the operational Create job lifecycle. */
    NotEligible = 'NotEligible',
    /** The appointment passed and nobody confirmed or moved it; the single Create job asks first. */
    NeedsDateDecision = 'NeedsDateDecision',
    NotFound = 'NotFound',
    /** Eligible, but the build itself threw; `message` carries the cause. */
    Failed = 'Failed',
}

export const VISIT_CREATE_JOB_SKIP_LABELS: Readonly<Record<VisitCreateJobSkip, string>> = {
    [VisitCreateJobSkip.HasJob]: 'This Visit already has a Job.',
    [VisitCreateJobSkip.MissingJobDetails]: 'This Visit is missing required Job details. Check its price book, owner and contact.',
    [VisitCreateJobSkip.NotApproved]: 'Approve this Visit’s tasks first.',
    [VisitCreateJobSkip.ApprovalChanged]: 'Tasks changed since approval. Approve them again first.',
    [VisitCreateJobSkip.PlanPaused]: 'Resume the Service Plan first.',
    [VisitCreateJobSkip.Skipped]: 'This Visit is marked Skipped. Restore it first.',
    [VisitCreateJobSkip.Missed]: 'Reschedule this missed Visit before creating its Job.',
    [VisitCreateJobSkip.NeedsReview]: 'Review this Visit before creating its Job.',
    [VisitCreateJobSkip.NoWork]: 'This Visit has no tasks or items to create a Job for.',
    [VisitCreateJobSkip.NotEligible]: 'This Visit is not eligible for Job creation.',
    [VisitCreateJobSkip.NeedsDateDecision]: 'Confirm or reschedule this Visit’s past appointment date, then try again.',
    [VisitCreateJobSkip.NotFound]: 'This Visit could not be found. Refresh the board.',
    [VisitCreateJobSkip.Failed]: 'The Job could not be created. View details.',
};

/** Board bulk payload: the selected rows' ids, as the single command takes `id`. */
export interface VisitCreateJobsRequest {
    ids: string[];
    reason?: string;
}

export interface VisitCreateJobSkipped {
    id: string;
    number?: string;
    reason: VisitCreateJobSkip;
    message?: string;
}

export interface VisitCreateJobsResult {
    created: number;
    skipped: VisitCreateJobSkipped[];
}

/** Undefined when Create job would build this row now; checked in the order a person would fix them. */
export function visitCreateJobSkip(row: VisitBrowseRow, now: Date): VisitCreateJobSkip | undefined {

    if (row.job?._id) return VisitCreateJobSkip.HasJob;
    const capabilities = visitBrowseCapabilities(row, now);
    if (!capabilities.dispatch) {
        if (row.status === 'Skipped') return VisitCreateJobSkip.Skipped;
        if (row.planPaused) return VisitCreateJobSkip.PlanPaused;
        if (row.ageState === VisitAgeState.Missed) return VisitCreateJobSkip.Missed;
        if (row.approvalStatus === TaskApprovalStatus.NeedsReview) return VisitCreateJobSkip.ApprovalChanged;
        if (!row.approvalStatus || row.approvalStatus === TaskApprovalStatus.NotApproved) return VisitCreateJobSkip.NotApproved;
        if (row.noLongerScheduled) return VisitCreateJobSkip.NeedsReview;
        if (row.detailsLoaded && !(row.taskCount ?? 0) && !row.items.length) return VisitCreateJobSkip.NoWork;
        return VisitCreateJobSkip.NotEligible;
    }
    if (row.jobReadinessDetail?.length) return VisitCreateJobSkip.MissingJobDetails;
    if (capabilities.needsDateDecision) return VisitCreateJobSkip.NeedsDateDecision;
    return undefined;
}
